import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { ReportType } from "../../abstract/Report";
import CircleLabel from "../../components/CircleLabel";
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
} from "../../components/Tables";
import { useApp } from "../../contexts/AppContext";
import { isRtlLanguage } from "../../utils";

export interface Data {
  image: string;
  index: number;
  score: number | null;
  fertilization: number;
  blastulation: number;
  invalid: boolean;
}

const StyledPaper = styled(Paper)(() => ({
  borderRadius: 2,
  backgroundColor: "white",
  ".MuiBox-root": {
    paddingLeft: 12,
    paddingRight: 12,
    marginBottom: 2,
    borderBottom: "1px solid #E5E5E5",
  },
}));

const AnalysisTable: React.FC<{ type?: ReportType; data: Data[] }> = ({
  data,
  type,
}) => {
  const { cycleId } = useParams();

  const navigate = useNavigate();

  const handleOpenOocyte = (item: Data) => {
    if (item.invalid) {
      return;
    }
    navigate(`/oocyte/${cycleId}/${item.index}`);
  };

  const { translate, currentLanguage } = useApp();
  const translations = useMemo(
    () => translate("oocyte").analysisTable,
    [translate]
  );

  console.log("oocyte: rendering AnalysisTable with data", data);

  return (
    <StyledTableContainer id="analysis-table" sx={{ border: "none" }}>
      <Table>
        <StyledTableHead>
          <TableRow>
            <TableCell>
              <Typography variant="report.table.normal">
                {translations.headers.image}
              </Typography>
            </TableCell>
            <TableCell width="5%">
              <Typography variant="report.table.normal">
                {translations.headers.oocyte}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="report.table.normal">
                {translations.headers.score}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="report.table.normal">
                {translations.headers.fertilizationProbability}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="report.table.normal">
                {translations.headers.blastulationProbability}
              </Typography>
            </TableCell>
            {type !== "report" && <TableCell></TableCell>}
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {data.map((item, index) => (
            <StyledTableRow
              key={index}
              onClick={() => {
                if (type !== "report") {
                  handleOpenOocyte(item);
                }
              }}
              sx={{
                backgroundColor: item.invalid ? "#F5F5F5" : "white",
                opacity: item.invalid ? 0.5 : 1,
              }}
            >
              <TableCell>
                <Box maxWidth={120}>
                  {item.image ? (
                    <img
                      style={{ borderRadius: 6 }}
                      width="100%"
                      height="100%"
                      src={item.image}
                      alt={translations.altText.oocyte}
                    />
                  ) : (
                    <CircularProgress />
                  )}
                </Box>
              </TableCell>
              <TableCell width="5%">
                <Typography variant="report.table.large">
                  {item.index}
                </Typography>
              </TableCell>
              <TableCell>
                <CircleLabel color="#F3E29B" width="40px">
                  <Typography variant="report.table.large" fontWeight="bold">
                    {item.score}
                  </Typography>
                </CircleLabel>
              </TableCell>
              <TableCell>
                <Typography variant="report.table.normal">
                  {item.fertilization
                    ? `${item.fertilization}%`
                    : translations.fallback.notAvailable}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="report.table.normal">
                  {item.blastulation
                    ? `${item.blastulation}%`
                    : translations.fallback.notAvailable}
                </Typography>
              </TableCell>
              {type !== "report" && (
                <TableCell>
                  <IconButton size="small" onClick={() => {}}>
                    <ArrowForwardIosIcon
                      sx={{
                        fontSize: 18,
                        transform: isRtlLanguage(currentLanguage)
                          ? "rotate(180deg)"
                          : "",
                      }}
                    />
                  </IconButton>
                </TableCell>
              )}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default AnalysisTable;
